import {
  TRAILER_DOCUMENT_FIELD_MAP,
  TRUCK_DOCUMENT_FIELD_MAP,
  VehicleType,
} from '@repo/shared'
import { Card, Skeleton } from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import PageHeader from '@/components/layout/page-header'
import EditTrailerForm from '@/features/vehicles/components/trailer/edit-trailer-form'
import EditTruckForm from '@/features/vehicles/components/truck/edit-truck-form'
import VehicleDocumentsSection from '@/features/vehicles/components/vehicle-documents-section'
import { useGetTrailerByVehicleId } from '@/features/vehicles/hooks/trailers/use-get-trailer-by-vehicle-id'
import { useGetTruckByVehicleId } from '@/features/vehicles/hooks/trucks/use-get-truck-by-vehicle-id'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

const editSearchParams = z.object({
  type: z.nativeEnum(VehicleType).optional().default(VehicleType.Truck),
})

export const Route = createFileRoute('/dashboard/vehicles/$vehicleId/edit')({
  head: () => ({ meta: [{ title: localizedPageTitle('editVehicle') }] }),
  component: EditVehiclePage,
  validateSearch: editSearchParams,
})

function EditVehiclePage() {
  useDocumentTitle('editVehicle')
  const { vehicleId } = Route.useParams()
  const { type } = Route.useSearch()
  const numericVehicleId = Number(vehicleId)

  if (type === VehicleType.Trailer) {
    return <EditTrailerPage vehicleId={numericVehicleId} />
  }

  return <EditTruckPage vehicleId={numericVehicleId} />
}

function EditTruckPage({ vehicleId }: { vehicleId: number }) {
  const { t } = useTranslation('vehicles')
  const {
    data: truckData,
    isError,
    isPending,
  } = useGetTruckByVehicleId(vehicleId)

  if (isPending) {
    return <EditSkeleton />
  }

  if (isError) {
    return (
      <div className="space-y-4 2xl:p-8">
        <Card className="gap-0 py-0 pt-4 pr-6 pb-6 pl-[26px]">
          <h2 className="text-secondary text-[32px] font-bold">
            {t('editTruck.notFound')}
          </h2>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 2xl:p-8">
      <PageHeader
        title={t('editTruck.title')}
        subtitle={t('editTruck.subtitle')}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[65fr_35fr]">
        <EditTruckForm vehicleId={vehicleId} initialData={truckData} />
        <VehicleDocumentsSection
          vehicleId={vehicleId}
          documentFieldMap={TRUCK_DOCUMENT_FIELD_MAP}
        />
      </div>
    </div>
  )
}

function EditTrailerPage({ vehicleId }: { vehicleId: number }) {
  const { t } = useTranslation('vehicles')
  const {
    data: trailerData,
    isError,
    isPending,
  } = useGetTrailerByVehicleId(vehicleId)

  if (isPending) {
    return <EditSkeleton />
  }

  if (isError) {
    return (
      <div className="space-y-4 2xl:p-8">
        <Card className="gap-0 py-0 pt-4 pr-6 pb-6 pl-[26px]">
          <h2 className="text-secondary text-[32px] font-bold">
            {t('editTrailer.notFound')}
          </h2>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 2xl:p-8">
      <PageHeader
        title={t('editTrailer.title')}
        subtitle={t('editTrailer.subtitle')}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[65fr_35fr]">
        <EditTrailerForm vehicleId={vehicleId} initialData={trailerData} />
        <VehicleDocumentsSection
          vehicleId={vehicleId}
          documentFieldMap={TRAILER_DOCUMENT_FIELD_MAP}
        />
      </div>
    </div>
  )
}

function EditSkeleton() {
  return (
    <div className="mx-auto max-w-6xl space-y-4 2xl:p-8">
      <Card className="gap-0 py-0 pt-4 pr-6 pb-6 pl-[26px]">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="mt-2 h-5 w-72" />
      </Card>
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    </div>
  )
}
